import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

/** One entry of this component's own table of contents, below. */
export interface LegalSection {
  id: string;
  heading: string;
}

export const legalSections: LegalSection[] = [
  { id: "publisher", heading: "1. Publisher and data controller" },
  { id: "scope", heading: "2. Scope" },
  { id: "personal-data", heading: "3. Personal data we process" },
  { id: "purposes", heading: "4. Purposes and legal basis" },
  { id: "recipients", heading: "5. Recipients" },
  { id: "transfers", heading: "6. International data transfers" },
  { id: "retention", heading: "7. Retention periods" },
  { id: "rights", heading: "8. Your rights" },
  { id: "security", heading: "9. Security measures" },
  { id: "obligations", heading: "10. Your obligations" },
  { id: "cookies", heading: "11. Cookies" },
  { id: "open-source", heading: "12. Open source software and source code" },
  { id: "document-info", heading: "13. Document information" },
];

/**
 * Body of the "Legal notices, privacy and security" modal. LegalNoticeDialog
 * renders only the dialog chrome (title bar, close controls); this
 * component renders its own table of contents (legalSections above) with
 * plain "#<section id>" anchor links, which the browser scrolls to within
 * the dialog's own scroll container (Dialog scroll="paper").
 *
 * Grounded strictly in this codebase: internal/sharing (member ID format,
 * no email collected), internal/portal (session and CSRF cookies, audit
 * log, OIDC staff auth), internal/scan and ADR 0003 (malware scanning),
 * ADR 0004 (Bedrock, never production data; see the "Status update,
 * 2026-09-11" note in that ADR for the one-account change), ADR
 * 0005 (HTTPS-only, TLS 1.2+), ADR 0007 (partner sharings, IP allowlist,
 * generated credentials shown once), ADR 0008 (portal architecture), and
 * infra/terraform (KMS encryption, S3 lifecycle rules, CloudTrail/audit
 * bucket Object Lock, RDS and AWS Backup retention, WAF header redaction).
 *
 * See docs/legal/privacy-and-security-notice.md for the same text with
 * source references next to every factual statement.
 *
 * Version 1.3, last updated 2026-09-13, approved by the manager on
 * 2026-09-13: see the "Document information" section at the end.
 */
export function LegalNoticeContent() {
  return (
    <Box>
      <Typography sx={{ mb: 3 }}>
        This notice explains who publishes and operates the Leeft Managed
        File Transfer service, what personal data it processes, why, for
        how long, and how it is protected. It also states your obligations
        as a user of the service and the open source licence terms that
        apply to part of it.
      </Typography>

      <Box component="nav" aria-label="Table of contents" sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Contents
        </Typography>
        <List dense disablePadding>
          {legalSections.map((section) => (
            <ListItem key={section.id} disablePadding sx={{ py: 0.25 }}>
              <ListItemText>
                <Link href={`#${section.id}`} underline="hover">
                  {section.heading}
                </Link>
              </ListItemText>
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Typography id="publisher" variant="h6" component="h2" gutterBottom>
        1. Publisher and data controller
      </Typography>
      <Typography sx={{ mb: 2 }}>
        The Leeft Managed File Transfer service ("the Service") is
        published, and the personal data described in this notice is
        controlled, by:
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem>
          <ListItemText
            primary="Legal entity"
            secondary="LEEFT, a French simplified joint-stock company (SAS), share capital 14,960 EUR."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Registered office"
            secondary="Aéroport Avignon Provence, 59 impasse Louis Blériot, Montfavet, 84140 Avignon, France."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Trade register"
            secondary="RCS Avignon 898 430 764 (SIREN 898 430 764)."
          />
        </ListItem>
        <ListItem>
          <ListItemText primary="Director of publication" secondary="Baptiste Sauvecanne, President." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Data protection contact" secondary="dpo@leeft.net" />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Hosting provider"
            secondary="Amazon Web Services EMEA SARL, 38 avenue John F. Kennedy, L-1855 Luxembourg. Data is stored in the eu-west-3 (Paris) region."
          />
        </ListItem>
      </List>

      <Typography id="scope" variant="h6" component="h2" gutterBottom>
        2. Scope
      </Typography>
      <Typography sx={{ mb: 2 }}>
        This notice covers the Service available at{" "}
        <Link href="https://mft.eu-west-3.leeftdigital.eu" target="_blank" rel="noopener noreferrer">
          mft.eu-west-3.leeftdigital.eu
        </Link>
        . It applies to Leeft staff who sign in to manage partner sharings
        and transfer files, and to partner organisation members who sign in
        to exchange files with Leeft. It does not cover any other Leeft
        website or service.
      </Typography>

      <Typography id="personal-data" variant="h6" component="h2" gutterBottom>
        3. Personal data we process
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem>
          <ListItemText
            primary="Member identifier"
            secondary='A generated ID such as "mft-exc-a6573". The Service does not collect or store a name or an email address for a partner account.'
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Partner organisation name"
            secondary='The display name of the "sharing" a member belongs to (for example the partner company name), used to identify the partner to Leeft staff.'
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Staff identity"
            secondary="For staff, the subject asserted by Leeft's single sign-on at sign-in, used only for audit and to detect repeated failed sign-ins (or, in non-production test environments only, a local username). Your display name from the identity provider is kept only in your encrypted session cookie, to show in the profile menu; it is never logged. Your email address is never read or stored."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Client IP address"
            secondary="Recorded on every request. Used to enforce a sharing's optional IP allowlist and to detect and block repeated failed sign-in attempts, and written to access and audit logs."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Timestamps"
            secondary="The time of each request, each sign-in, and each audited administrative action."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="File names and file contents"
            secondary="The files partners and staff exchange through the Service."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Security events"
            secondary="The outcome of the automated malware scan of every file a partner uploads (clean, infected, oversized, or otherwise unscannable), and the reason when a file is quarantined."
          />
        </ListItem>
      </List>

      <Typography id="purposes" variant="h6" component="h2" gutterBottom>
        4. Purposes and legal basis
      </Typography>
      <List dense sx={{ mb: 1 }}>
        <ListItem>
          <ListItemText
            primary="Providing the Service"
            secondary="Creating and managing sharings and members, authenticating staff and partners, and transferring files: performance of the contract or business relationship with the partner organisation, or Leeft's legitimate interest in operating the Service for its own staff."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Securing the Service"
            secondary="Malware scanning of every uploaded file, IP allowlisting, brute-force protection and web application firewall protection: Leeft's legitimate interest in protecting its systems and its partners' data."
          />
        </ListItem>
        <ListItem>
          <ListItemText
            primary="Keeping an audit trail"
            secondary="Logging staff actions (creating or closing a sharing, adding or removing a member, changing an allowlist) and security events: Leeft's legitimate interest in accountability and, where applicable, a legal obligation to retain records."
          />
        </ListItem>
      </List>

      <Typography id="recipients" variant="h6" component="h2" gutterBottom>
        5. Recipients
      </Typography>
      <List dense sx={{ mb: 1 }}>
        <ListItem>
          <ListItemText primary="Leeft staff authorised to manage the Service, on a need-to-know basis." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Amazon Web Services EMEA SARL, as the Service's hosting provider (infrastructure and storage only)." />
        </ListItem>
      </List>
      <Typography sx={{ mb: 2 }}>
        No other processor is used to operate the Service: the Service is
        designed to collect no partner email address, so no separate
        provider is used to send one. Data is not sold and is not used for
        marketing.
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Leeft may disclose data where required by law, for example in
        response to a valid legal or judicial request.
      </Typography>

      <Typography id="transfers" variant="h6" component="h2" gutterBottom>
        6. International data transfers
      </Typography>
      <Typography sx={{ mb: 2 }}>
        The Service and the personal data it processes are hosted in the
        European Union, in AWS's eu-west-3 (Paris) region.
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Leeft's build pipeline uses AI models hosted on AWS Bedrock, within
        the EU, for two automated tasks: reviewing code changes and
        triaging the deterministic findings of an AI-assisted penetration
        test run against a local, disposable test environment the pipeline
        itself creates for that purpose. The code-review task currently
        uses a Mistral model; the penetration-test triage task uses
        several models independently, currently from Amazon and Mistral,
        to cross-check the same results. Anthropic's Claude is supported
        for both tasks but is not currently invoked. Production data,
        including any personal data processed by the Service, never
        enters this pipeline.
      </Typography>
      <Typography sx={{ mb: 2 }}>
        No personal data processed by the Service is transferred outside
        the European Union.
      </Typography>

      <Typography id="retention" variant="h6" component="h2" gutterBottom>
        7. Retention periods
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Files and records are kept only for as long as needed for the
        purposes above, then deleted automatically:
      </Typography>
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Data</TableCell>
            <TableCell>Retention period</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Files awaiting malware scan (superseded versions)</TableCell>
            <TableCell>1 day</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Files delivered to Leeft staff</TableCell>
            <TableCell>90 days</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Files sent by Leeft to a partner</TableCell>
            <TableCell>90 days</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Quarantined (infected or unscannable) files</TableCell>
            <TableCell>30 days</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Superseded file versions not covered above</TableCell>
            <TableCell>30 days</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Incomplete uploads</TableCell>
            <TableCell>Aborted after 2 days</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Application logs (portal, transfer server, scanner)</TableCell>
            <TableCell>90 days</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Audit trail (staff actions and security events)</TableCell>
            <TableCell>365 days, in a tamper-resistant, write-once archive</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Database backups, point-in-time recovery</TableCell>
            <TableCell>7 days</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Database backups, long-term</TableCell>
            <TableCell>35 days</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Typography color="text.secondary" sx={{ fontStyle: "italic", mb: 2 }}>
        These are the Service's configured values as of the date of this
        notice. See docs/legal/privacy-and-security-notice.md for the exact
        configuration each value comes from.
      </Typography>

      <Typography id="rights" variant="h6" component="h2" gutterBottom>
        8. Your rights
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Under the General Data Protection Regulation, you have the right to
        access the personal data Leeft holds about you, to have inaccurate
        data corrected, to have it erased, to restrict or object to its
        processing, and to receive it in a portable format, in each case
        subject to the conditions the GDPR sets for that right. To exercise
        any of these rights, contact dpo@leeft.net.
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Leeft is a French company, so the competent supervisory authority
        is the French data protection authority, the Commission Nationale
        de l'Informatique et des Libertés (CNIL). To lodge a complaint, go
        to{" "}
        <Link href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">
          www.cnil.fr
        </Link>
        .
      </Typography>

      <Typography id="security" variant="h6" component="h2" gutterBottom>
        9. Security measures
      </Typography>
      <List dense sx={{ mb: 2 }}>
        <ListItem>
          <ListItemText primary="Encryption in transit: TLS 1.2 or higher on every connection to the Service." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Encryption at rest: every file, database and backup is encrypted with a dedicated AWS KMS key." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Malware scanning: every file a partner uploads is scanned before any Leeft staff member can access it." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Optional IP allowlist: a partner organisation can be restricted to sign in only from agreed IP addresses." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Brute-force protection: an IP address is automatically blocked after repeated failed sign-in attempts, and login pages carry additional rate limiting." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Strong generated passwords: every new partner account starts with a random, 20-character password generated by the platform, which the account holder can change at any time." />
        </ListItem>
        <ListItem>
          <ListItemText primary="No stored passwords: a generated partner password is shown once, at creation or reset, and is never stored, displayed again or logged by the Service afterward." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Audit logging: every staff administrative action is recorded and shipped to a tamper-resistant, write-once storage area." />
        </ListItem>
        <ListItem>
          <ListItemText primary="Least-privilege access: Leeft staff act through scoped service accounts, never a personal account with direct access to the underlying transfer server." />
        </ListItem>
      </List>

      <Typography id="obligations" variant="h6" component="h2" gutterBottom>
        10. Your obligations
      </Typography>
      <Typography sx={{ mb: 2 }}>
        As a user of the Service, you must keep your credentials
        confidential and never share your account with anyone else. You
        may only upload files you are lawfully entitled to share and that
        relate to your business relationship with Leeft, and you must never
        attempt to upload malware or otherwise abuse the Service. If you
        suspect your account has been compromised, contact dpo@leeft.net,
        or your usual Leeft contact, immediately. Leeft may suspend access
        for any account it reasonably believes is being misused.
      </Typography>

      <Typography id="cookies" variant="h6" component="h2" gutterBottom>
        11. Cookies
      </Typography>
      <Typography sx={{ mb: 2 }}>
        The Service uses only two cookies, both strictly necessary for it
        to function. Neither is used for analytics, advertising or
        tracking, so no cookie consent banner is shown.
      </Typography>
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Cookie</TableCell>
            <TableCell>Purpose</TableCell>
            <TableCell>Lifetime</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>
              <code>__Host-mft_session</code>
            </TableCell>
            <TableCell>Keeps you signed in.</TableCell>
            <TableCell>
              Deleted when you close your browser. Your sign-in also stops
              working after about 60 minutes regardless, since it is not
              refreshed automatically.
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <code>mft_csrf</code>
            </TableCell>
            <TableCell>Protects the Service against cross-site request forgery.</TableCell>
            <TableCell>Deleted when you close your browser.</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Typography id="open-source" variant="h6" component="h2" gutterBottom>
        12. Open source software and source code
      </Typography>
      <Typography id="open-source-software" variant="subtitle1" component="h3" gutterBottom>
        Open source software
      </Typography>
      <Typography sx={{ mb: 2 }}>
        This service is built on <strong>SFTPGo</strong>, copyright (c)
        Nicola Murino and the SFTPGo contributors,{" "}
        <Link href="https://github.com/drakkan/sftpgo" target="_blank" rel="noopener noreferrer">
          https://github.com/drakkan/sftpgo
        </Link>
        . SFTPGo is not affiliated with and does not endorse Leeft or this
        service.
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Leeft builds this service's SFTPGo image from SFTPGo's own
        unmodified upstream Community source. Nothing in this deployment
        forks or patches SFTPGo's source code: every difference from a
        default installation, including the visual branding on this page
        and on the sign-in pages, is applied through SFTPGo's own
        configuration file and documented static assets (branding name,
        logo, favicon and an additional CSS file), not through source
        changes or template overrides. The image is compiled with
        current, security-updated versions of a small number of SFTPGo's
        own Go module dependencies (see "Corresponding source" below);
        this updates the libraries the unmodified source is built
        against, not the source itself.
      </Typography>
      <Typography id="licence" variant="subtitle1" component="h3" gutterBottom>
        Licence
      </Typography>
      <Typography sx={{ mb: 2 }}>
        SFTPGo is licensed under the GNU Affero General Public License
        version 3 (AGPL-3.0-only) with additional terms, as stated in the{" "}
        <Link
          href="https://github.com/drakkan/sftpgo/blob/main/NOTICE"
          target="_blank"
          rel="noopener noreferrer"
        >
          NOTICE file
        </Link>{" "}
        of the SFTPGo source repository. The full licence text is available
        at{" "}
        <Link
          href="https://www.gnu.org/licenses/agpl-3.0.en.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://www.gnu.org/licenses/agpl-3.0.en.html
        </Link>
        .
      </Typography>
      <Typography id="corresponding-source" variant="subtitle1" component="h3" gutterBottom>
        Corresponding source
      </Typography>
      <Typography sx={{ mb: 2 }}>
        This service runs SFTPGo version 2.7.5, built from unmodified
        upstream source with security-updated dependencies (see{" "}
        <code>docker/sftpgo/README.md</code> for exactly which
        dependencies and why). The exact SFTPGo source corresponding to
        the deployed version is available at{" "}
        <Link
          href="https://github.com/drakkan/sftpgo/tree/v2.7.5"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://github.com/drakkan/sftpgo/tree/v2.7.5
        </Link>
        . This link is updated at every SFTPGo version upgrade.
      </Typography>

      <Typography id="document-info" variant="h6" component="h2" gutterBottom>
        13. Document information
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Version 1.3. Last updated 13 September 2026: section 6 corrected
        to name the AI providers actually invoked by the build pipeline -
        Amazon and Mistral models today, hosted on AWS Bedrock in the EU;
        Anthropic's Claude remains supported for both pipeline tasks but
        is not currently invoked - replacing the previous wording, which
        named only Claude. No other section changed. Approved by the
        manager on 13 September 2026.
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Version 1.2. Last updated 12 September 2026: section 12 corrected
        to describe the SFTPGo image as built from unmodified upstream
        source with security-updated dependencies, instead of the
        prebuilt image the previous wording described; no other section
        changed. Version 1.1 was approved by Leeft's legal department on
        12 September 2026.
      </Typography>
    </Box>
  );
}
